/**
 * The task routes (slice 2b spec §8) and their query keys. `['tasks']` is the
 * list (every tab under it), `['task', id]` one task; the `task` hub event
 * invalidates both, and the task's thread (`app/events.ts`).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../app/api";
import { meKey } from "../../app/me";
import { threadKey, threadsKey, type Thread, type ThreadMessage, type Turn } from "../chats/api";
import type { AreaRef } from "../areas/api";
import type { DocItem } from "../docs/api";

export type TaskStage = "queued" | "working" | "waiting" | "done" | "stuck" | "canceled";
export type TaskFilter = "open" | "waiting" | "done" | "canceled";
export const TASK_FILTERS: readonly TaskFilter[] = ["open", "waiting", "done", "canceled"];

export type TaskItem = {
  id: string;
  title: string;
  stage: TaskStage;
  waiting_on_text: string | null;
  /** Slice 4 §5: the person here it waits on, and the answers offered them. Missing on an older Worker. */
  waiting_on_user_id?: string | null;
  options?: string[];
  ongoing: boolean;
  /** The latest note, else what done looks like, cut to 120 characters. */
  detail: string;
  agent: { id: string; slug: string; name: string; glyph: string; tint: string };
  for: { id: string; name: string; initials: string; tint: string } | null;
  thread_id: string;
  created_at: string;
  /** The task's last event. */
  updated_at: string;
  done_at: string | null;
  /** The day it's needed by, `YYYY-MM-DD` in the workspace's timezone; null with none. Missing on an older Worker. */
  due?: string | null;
  /** Its Area's chip (slice 6d spec §4.4), null with none. Missing on an older Worker. */
  area?: AreaRef | null;
  area_id?: string | null;
};

export type TaskEventKind =
  | "created"
  | "stage"
  | "note"
  | "dispatched"
  | "nudged"
  | "edited"
  /** Slice 3 §4.3: `data: { approval_id, status }`, the status as it closed. */
  | "approval_opened"
  | "approval_closed";

export type TaskEvent = {
  id: string;
  kind: TaskEventKind;
  text: string;
  data: Record<string, unknown>;
  actor: { kind: "person" | "agent" | "crew"; id: string | null; name: string };
  turn_id: string | null;
  created_at: string;
};

export type TaskReceipts = {
  turns: number;
  tokens: number;
  computer_ms: number;
  web: number;
  shared: boolean;
};

export type TaskDetail = {
  task: TaskItem & { done_looks_like: string; last_stage_at: string; nudged_at: string | null };
  events: TaskEvent[];
  thread: { id: string; title: string } | null;
  /** Its docs, archived ones left out (slice 2d spec §11.3). */
  docs?: DocItem[];
  /** The owner's; null for a member. */
  receipts: TaskReceipts | null;
};

export type TaskCounts = Record<TaskFilter, number>;
type TasksPage = { items: TaskItem[]; counts: TaskCounts; next_cursor: string | null };

export const tasksKey = ["tasks"] as const;
export const taskKey = (id: string) => ["task", id] as const;

function tasksPath(filter: TaskFilter, cursor: string | null, area: string | null): string {
  const q = new URLSearchParams({ filter });
  if (area) q.set("area", area);
  if (cursor) q.set("cursor", cursor);
  return `/api/tasks?${q.toString()}`;
}

/** One tab of the Tasks page, 50 a page, filtered to an Area's slug or `none` (slice 6d). */
export function useTasks(filter: TaskFilter, area: string | null = null) {
  return useInfiniteQuery({
    queryKey: area ? [...tasksKey, filter, area] : [...tasksKey, filter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<TasksPage>("GET", tasksPath(filter, pageParam, area)),
    getNextPageParam: (page) => page.next_cursor,
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKey(id),
    queryFn: () => api<TaskDetail>("GET", `/api/tasks/${id}`),
  });
}

export type NewTask = {
  title: string;
  done_looks_like?: string;
  /** Slice 6d (spec §4.2): made on an Area's page. */
  area_id?: string;
};
type Started = { thread: Thread; task: TaskItem; message: ThreadMessage; turn: Turn };

/** New task (spec §8.3): a thread, the person's card, a Queued task and a turn. */
export function useCreateTask() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NewTask) => api<Started>("POST", "/api/tasks", input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: tasksKey });
      void client.invalidateQueries({ queryKey: threadsKey });
      void client.invalidateQueries({ queryKey: ["area"] });
    },
    onError: (error) => {
      // `409 ai_not_connected`: the connection changed since `/api/me` was read.
      if ("code" in error && error.code === "ai_not_connected") {
        void client.invalidateQueries({ queryKey: meKey });
      }
    },
  });
}

export type TaskEdit = {
  title?: string;
  ongoing?: boolean;
  stage?: "done" | "canceled" | "queued";
  note?: string;
  /** Slice 6d (spec §4.2): another Area, or none. */
  area_id?: string | null;
};

/** A person's change (spec §8.4): Mark done, Cancel, Reopen, rename, Ongoing, its Area (slice 6d). */
export function useEditTask(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (edit: TaskEdit) => api<{ task: TaskItem }>("PATCH", `/api/tasks/${id}`, edit),
    onSuccess: ({ task }) => {
      void client.invalidateQueries({ queryKey: taskKey(id) });
      void client.invalidateQueries({ queryKey: tasksKey });
      void client.invalidateQueries({ queryKey: threadKey(task.thread_id) });
      // Its Area's page and the sidebar's open counts (slice 6d).
      void client.invalidateQueries({ queryKey: ["area"] });
      void client.invalidateQueries({ queryKey: meKey });
    },
  });
}
